/**
 * SectionRenderer — single dispatcher that renders any of the 18 supported
 * sections from a `template_snapshot.layout_json.sections` list against
 * a `content_json` payload.
 *
 * Each section is a small inline component below to keep it all in one file.
 * Style (theme/primary/accent/fonts) is applied via CSS variables so each
 * template feels visually distinct without per-template components.
 */
import { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, ChevronRight, Star, Search, Play, Bot } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Public entry point ────────────────────────────────────────────────────

export default function SectionRenderer({ snapshot, content, slug }) {
  if (!snapshot || !content) return null;
  const sections = snapshot?.layout_json?.sections || [];
  const style = snapshot?.layout_json?.style || {};
  const isDark = style.theme === 'dark';

  const cssVars = {
    '--site-primary':       style.primary || (isDark ? '#0B1020' : '#FFFFFF'),
    '--site-accent':        style.accent  || '#111827',
    '--site-text':          isDark ? '#F9FAFB' : '#0F172A',
    '--site-text-muted':    isDark ? '#94A3B8' : '#475569',
    '--site-border':        isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.10)',
    '--site-card':          isDark ? 'rgba(255,255,255,0.04)' : '#FFFFFF',
    '--site-font-heading':  `"${style.font_heading || 'Inter'}", serif`,
    '--site-font-body':     `"${style.font_body    || 'Inter'}", sans-serif`,
  };

  return (
    <div
      data-testid="section-renderer"
      style={{
        ...cssVars,
        backgroundColor: 'var(--site-primary)',
        color:           'var(--site-text)',
        fontFamily:      'var(--site-font-body)',
        minHeight:       '100vh',
      }}>
      {sections.map((id, i) => {
        const Cmp = SECTION_MAP[id] || UnknownSection;
        return <Cmp key={`${id}-${i}`} content={content} slug={slug} />;
      })}
    </div>
  );
}

// ── Shared building blocks ─────────────────────────────────────────────────

const Section = ({ children, className = '', testId }) => (
  <section data-testid={testId}
           className={`relative px-6 sm:px-10 py-16 sm:py-24 ${className}`}>
    <div className="mx-auto max-w-6xl">{children}</div>
  </section>
);

const Heading = ({ children, level = 2, className = '' }) => {
  const Tag = `h${level}`;
  return (
    <Tag className={`tracking-tight ${className}`}
         style={{ fontFamily: 'var(--site-font-heading)' }}>
      {children}
    </Tag>
  );
};

const AccentButton = ({ href, onClick, children, testId }) => (
  <a href={href} onClick={onClick} data-testid={testId}
     className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium transition hover:opacity-90"
     style={{ background: 'var(--site-accent)', color: '#fff' }}>
    {children}
  </a>
);

// ── Sections ───────────────────────────────────────────────────────────────

function HeroImage({ content }) {
  const h = content.hero || {};
  return (
    <Section testId="section-hero_image" className="text-center">
      <p className="text-xs uppercase tracking-[0.2em] mb-3 opacity-70">{h.eyebrow}</p>
      <Heading level={1} className="text-4xl sm:text-5xl lg:text-6xl font-semibold mb-4">
        {h.headline || content.branding?.business_name}
      </Heading>
      <p className="text-base sm:text-lg max-w-2xl mx-auto mb-8 opacity-80">{h.subheadline}</p>
      {(h.cta_phone || h.cta_label) && (
        <AccentButton href={h.cta_phone ? `tel:${h.cta_phone}` : '#contact'} testId="hero-cta">
          <Phone size={16} /> {h.cta_label || 'Call now'}
        </AccentButton>
      )}
    </Section>
  );
}

function HeroVideo({ content }) {
  const h = content.hero || {};
  return (
    <Section testId="section-hero_video" className="text-center">
      <div className="rounded-2xl overflow-hidden mb-10 aspect-video flex items-center justify-center"
           style={{ background: 'var(--site-card)', border: '1px solid var(--site-border)' }}>
        <Play size={56} style={{ color: 'var(--site-accent)' }} />
      </div>
      <Heading level={1} className="text-4xl sm:text-5xl font-semibold mb-3">{h.headline}</Heading>
      <p className="text-lg max-w-2xl mx-auto opacity-80 mb-6">{h.subheadline}</p>
      {h.cta_phone && <AccentButton href={`tel:${h.cta_phone}`} testId="hero-video-cta">
        <Phone size={16} /> {h.cta_label}
      </AccentButton>}
    </Section>
  );
}

function HeroForm({ content }) {
  const h = content.hero || {};
  return (
    <Section testId="section-hero_form" className="grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <Heading level={1} className="text-3xl sm:text-5xl font-semibold mb-4">{h.headline}</Heading>
        <p className="text-base opacity-80 mb-6">{h.subheadline}</p>
      </div>
      <ContactCard content={content} compact />
    </Section>
  );
}

function SearchBar({ content }) {
  const s = content.search_bar || {};
  return (
    <Section testId="section-search_bar" className="text-center">
      <div className="mx-auto max-w-2xl flex gap-2 rounded-full p-2"
           style={{ background: 'var(--site-card)', border: '1px solid var(--site-border)' }}>
        <Search size={20} className="my-auto ml-3 opacity-60" />
        <input type="text" placeholder={s.placeholder}
               className="flex-1 bg-transparent outline-none px-2 text-sm" />
        <button className="rounded-full px-5 py-2 text-sm font-medium"
                style={{ background: 'var(--site-accent)', color: '#fff' }}>
          {s.cta_label || 'Search'}
        </button>
      </div>
    </Section>
  );
}

function MapSearch({ content }) {
  const m = content.map_search || {};
  return (
    <Section testId="section-map_search">
      <Heading level={2} className="text-2xl sm:text-3xl font-semibold mb-3">{m.label}</Heading>
      <p className="opacity-70 mb-6">{m.hint}</p>
      <div className="rounded-2xl aspect-[16/7] flex items-center justify-center"
           style={{ background: 'var(--site-card)', border: '1px solid var(--site-border)' }}>
        <MapPin size={48} style={{ color: 'var(--site-accent)' }} />
      </div>
    </Section>
  );
}

function ListingsGrid({ content }) {
  const items = content.listings || [];
  return (
    <Section testId="section-listings_grid">
      <Heading level={2} className="text-2xl sm:text-3xl font-semibold mb-8">Featured listings</Heading>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((l, i) => (
          <article key={i}
                   className="rounded-xl overflow-hidden"
                   style={{ background: 'var(--site-card)', border: '1px solid var(--site-border)' }}>
            <div className="aspect-[4/3]" style={{ background: 'var(--site-accent)', opacity: 0.15 }} />
            <div className="p-4">
              <p className="text-lg font-semibold" style={{ color: 'var(--site-accent)' }}>{l.price}</p>
              <p className="text-sm font-medium">{l.title}</p>
              <p className="text-xs opacity-60 mt-1">{l.subtitle}</p>
            </div>
          </article>
        ))}
      </div>
    </Section>
  );
}

function AgentProfile({ content }) {
  const a = content.agent_profile || {};
  return (
    <Section testId="section-agent_profile" className="grid lg:grid-cols-3 gap-8 items-center">
      <div className="rounded-2xl aspect-square"
           style={{ background: 'var(--site-card)', border: '1px solid var(--site-border)' }} />
      <div className="lg:col-span-2">
        <Heading level={2} className="text-3xl font-semibold mb-1">{a.name}</Heading>
        <p className="text-sm opacity-70 mb-4">{a.title}</p>
        <p className="leading-relaxed opacity-90">{a.bio}</p>
      </div>
    </Section>
  );
}

function TeamGrid({ content }) {
  const team = content.team || [];
  return (
    <Section testId="section-team_grid">
      <Heading level={2} className="text-3xl font-semibold mb-8">Meet the team</Heading>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {team.map((m, i) => (
          <div key={i} className="rounded-xl p-6 text-center"
               style={{ background: 'var(--site-card)', border: '1px solid var(--site-border)' }}>
            <div className="rounded-full mx-auto mb-3 w-20 h-20"
                 style={{ background: 'var(--site-accent)', opacity: 0.15 }} />
            <p className="font-semibold">{m.name}</p>
            <p className="text-xs opacity-70">{m.role}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Testimonials({ content }) {
  const items = content.testimonials || [];
  return (
    <Section testId="section-testimonials">
      <Heading level={2} className="text-3xl font-semibold mb-10 text-center">What clients say</Heading>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {items.map((t, i) => (
          <blockquote key={i} className="rounded-xl p-6"
                      style={{ background: 'var(--site-card)', border: '1px solid var(--site-border)' }}>
            <div className="flex gap-0.5 mb-3" style={{ color: 'var(--site-accent)' }}>
              {Array.from({ length: t.rating || 5 }).map((_, j) => <Star key={j} size={14} fill="currentColor" />)}
            </div>
            <p className="text-sm leading-relaxed mb-4">"{t.quote}"</p>
            <footer className="text-xs opacity-70">— {t.author}</footer>
          </blockquote>
        ))}
      </div>
    </Section>
  );
}

function VideoGallery({ content }) {
  const items = content.video_gallery || [];
  return (
    <Section testId="section-video_gallery">
      <Heading level={2} className="text-3xl font-semibold mb-8">Video tours & insights</Heading>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {items.map((v, i) => (
          <div key={i} className="rounded-xl overflow-hidden"
               style={{ background: 'var(--site-card)', border: '1px solid var(--site-border)' }}>
            <div className="aspect-video flex items-center justify-center">
              <Play size={36} style={{ color: 'var(--site-accent)' }} />
            </div>
            <p className="p-3 text-sm">{v.title}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Stats({ content }) {
  const items = content.stats || [];
  return (
    <Section testId="section-stats">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
        {items.map((s, i) => (
          <div key={i}>
            <p className="text-3xl sm:text-4xl font-bold"
               style={{ color: 'var(--site-accent)', fontFamily: 'var(--site-font-heading)' }}>
              {s.value}
            </p>
            <p className="text-xs uppercase tracking-wide opacity-70 mt-1">{s.label}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Neighborhoods({ content }) {
  const items = content.neighborhoods || [];
  return (
    <Section testId="section-neighborhoods">
      <Heading level={2} className="text-3xl font-semibold mb-8">Neighborhoods we serve</Heading>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((n, i) => (
          <div key={i} className="rounded-xl p-5"
               style={{ background: 'var(--site-card)', border: '1px solid var(--site-border)' }}>
            <p className="font-semibold mb-1">{n.name}</p>
            <p className="text-xs opacity-70">{n.description}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function ProcessSteps({ content }) {
  const items = content.process_steps || [];
  return (
    <Section testId="section-process_steps">
      <Heading level={2} className="text-3xl font-semibold mb-10 text-center">How it works</Heading>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {items.map((s, i) => (
          <div key={i} className="rounded-xl p-6"
               style={{ background: 'var(--site-card)', border: '1px solid var(--site-border)' }}>
            <p className="text-3xl font-bold mb-3"
               style={{ color: 'var(--site-accent)' }}>{String(s.step).padStart(2, '0')}</p>
            <p className="font-semibold mb-1">{s.title}</p>
            <p className="text-sm opacity-70">{s.description}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function FAQ({ content }) {
  const items = content.faqs || [];
  return (
    <Section testId="section-faq">
      <Heading level={2} className="text-3xl font-semibold mb-8 text-center">Frequently asked</Heading>
      <div className="max-w-3xl mx-auto divide-y" style={{ borderColor: 'var(--site-border)' }}>
        {items.map((f, i) => (
          <details key={i} className="py-4">
            <summary className="cursor-pointer font-medium flex items-center justify-between">
              <span>{f.q}</span>
              <ChevronRight size={16} className="opacity-60" />
            </summary>
            <p className="mt-2 text-sm opacity-80 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </Section>
  );
}

function ContactCard({ content, compact }) {
  const c = content.contact_form || {};
  const phone = content.branding?.phone;
  const slug = content?._slug; // optional
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Reuse existing public lead-form endpoint
      await axios.post(`${API}/website-leads`, {
        slug, ...form, source: 'website-template-contact-form',
      });
      setOk(true);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch { /* swallow — show generic confirmation */ setOk(true); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit}
          className={`rounded-2xl p-6 ${compact ? '' : 'max-w-xl mx-auto'}`}
          style={{ background: 'var(--site-card)', border: '1px solid var(--site-border)' }}>
      {!compact && <Heading level={2} className="text-2xl font-semibold mb-1">{c.headline}</Heading>}
      {!compact && <p className="text-sm opacity-70 mb-5">{c.subheadline}</p>}
      <div className="grid grid-cols-1 gap-3">
        <input required placeholder="Your name" value={form.name}
               onChange={(e) => setForm({ ...form, name: e.target.value })}
               className="rounded-lg px-3 py-2 text-sm bg-transparent outline-none"
               style={{ border: '1px solid var(--site-border)' }} />
        <input required type="email" placeholder="Email" value={form.email}
               onChange={(e) => setForm({ ...form, email: e.target.value })}
               className="rounded-lg px-3 py-2 text-sm bg-transparent outline-none"
               style={{ border: '1px solid var(--site-border)' }} />
        <input placeholder="Phone (optional)" value={form.phone}
               onChange={(e) => setForm({ ...form, phone: e.target.value })}
               className="rounded-lg px-3 py-2 text-sm bg-transparent outline-none"
               style={{ border: '1px solid var(--site-border)' }} />
        <textarea rows={3} placeholder="How can we help?" value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="rounded-lg px-3 py-2 text-sm bg-transparent outline-none"
                  style={{ border: '1px solid var(--site-border)' }} />
      </div>
      <button type="submit" disabled={busy}
              className="w-full mt-4 rounded-full py-3 font-medium"
              data-testid="contact-submit"
              style={{ background: 'var(--site-accent)', color: '#fff' }}>
        {busy ? 'Sending…' : ok ? 'Thank you — we\'ll be in touch' : (c.cta || 'Send message')}
      </button>
      {phone && <p className="text-center text-xs opacity-60 mt-3">
        Or call <a className="underline" href={`tel:${phone}`}>{phone}</a></p>}
    </form>
  );
}

function ContactForm({ content, slug }) {
  // Inject slug for the lead-form payload
  const enriched = { ...content, _slug: slug };
  return (
    <Section testId="section-contact_form" className="grid lg:grid-cols-2 gap-12">
      <div>
        <Heading level={2} className="text-3xl font-semibold mb-3">{content.contact_form?.headline}</Heading>
        <p className="opacity-80 mb-6">{content.contact_form?.subheadline}</p>
        {content.branding?.phone && (
          <p className="flex items-center gap-2"><Phone size={14}/>{content.branding.phone}</p>)}
        {content.branding?.email && (
          <p className="flex items-center gap-2 mt-1"><Mail size={14}/>{content.branding.email}</p>)}
      </div>
      <ContactCard content={enriched} />
    </Section>
  );
}

function CtaBanner({ content }) {
  const c = content.cta_banner || {};
  return (
    <Section testId="section-cta_banner" className="text-center">
      <div className="rounded-2xl p-10"
           style={{ background: 'var(--site-accent)', color: '#fff' }}>
        <Heading level={2} className="text-2xl sm:text-4xl font-semibold mb-3">{c.headline}</Heading>
        <p className="opacity-90 mb-6">{c.subheadline}</p>
        {c.cta_phone && (
          <a href={`tel:${c.cta_phone}`} data-testid="cta-banner-phone"
             className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-white font-medium"
             style={{ color: 'var(--site-accent)' }}>
            <Phone size={16} /> {c.cta_label || c.cta_phone}
          </a>)}
      </div>
    </Section>
  );
}

function ChatAi({ content }) {
  const c = content.chat_ai || {};
  return (
    <Section testId="section-chat_ai">
      <div className="rounded-2xl p-8 grid lg:grid-cols-[auto_1fr] gap-6 items-center"
           style={{ background: 'var(--site-card)', border: '1px solid var(--site-border)' }}>
        <div className="rounded-xl p-5"
             style={{ background: 'var(--site-accent)', color: '#fff' }}>
          <Bot size={36} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] opacity-70 mb-1">AI assistant</p>
          <Heading level={2} className="text-2xl font-semibold mb-2">{c.assistant_name}</Heading>
          <p className="opacity-90 mb-4">{c.intro}</p>
          {c.phone && <AccentButton href={`tel:${c.phone}`} testId="chat-ai-phone">
            <Phone size={16} /> Talk to {c.assistant_name}
          </AccentButton>}
        </div>
      </div>
    </Section>
  );
}

function Footer({ content }) {
  const f = content.footer || {};
  return (
    <footer data-testid="section-footer"
            className="px-6 sm:px-10 py-10 mt-10 text-sm"
            style={{ borderTop: '1px solid var(--site-border)' }}>
      <div className="mx-auto max-w-6xl flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <p className="font-semibold">{f.business_name}</p>
          <p className="opacity-70">{f.tagline}</p>
        </div>
        <div className="text-right opacity-80 text-xs">
          {f.phone && <p>{f.phone}</p>}
          {f.email && <p>{f.email}</p>}
          <p className="mt-2 opacity-60">© {f.year} {f.business_name} · Powered by Agent9</p>
        </div>
      </div>
    </footer>
  );
}

function UnknownSection({ content: _c }) { return null; }

const SECTION_MAP = {
  hero_image: HeroImage,
  hero_video: HeroVideo,
  hero_form:  HeroForm,
  search_bar: SearchBar,
  map_search: MapSearch,
  listings_grid: ListingsGrid,
  agent_profile: AgentProfile,
  team_grid:  TeamGrid,
  testimonials: Testimonials,
  video_gallery: VideoGallery,
  stats: Stats,
  neighborhoods: Neighborhoods,
  process_steps: ProcessSteps,
  faq: FAQ,
  contact_form: ContactForm,
  cta_banner: CtaBanner,
  chat_ai: ChatAi,
  footer: Footer,
};
